import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, AlertTriangle, AlertCircle, Info, Check, X, Eye, Filter, Search } from 'lucide-react';
import { useAlerts } from '../../hooks/useAlerts';
import './AlertManagement.css';

/**
 * Alert Management Page
 * Full-page view for managing all alerts with history
 */
const AlertManagement = ({ userId, userName }) => {
  const { t } = useTranslation('patientRisk');
  const [statusFilter, setStatusFilter] = useState('ACTIVE'); // 'ACTIVE', 'RESOLVED', 'DISMISSED', 'all'
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  
  const { 
    alerts, 
    loading, 
    counts, 
    markAsRead, 
    acknowledge, 
    dismiss,
    resolve,
    refresh 
  } = useAlerts({ 
    userId, 
    activeOnly: statusFilter === 'ACTIVE',
    autoRefresh: statusFilter === 'ACTIVE'
  });
  
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return <AlertTriangle size={24} color="#f44336" />;
      case 'HIGH':
        return <AlertCircle size={24} color="#ff9800" />;
      case 'MEDIUM':
        return <Info size={24} color="#ffc107" />;
      default:
        return <Bell size={24} color="#2196f3" />;
    }
  };
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return '#f44336';
      case 'HIGH':
        return '#ff9800';
      case 'MEDIUM':
        return '#ffc107';
      default:
        return '#2196f3';
    }
  };
  
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'status-active';
      case 'RESOLVED':
        return 'status-resolved';
      case 'DISMISSED':
        return 'status-dismissed';
      default:
        return '';
    }
  };
  
  const handleAlertClick = async (alert) => {
    setSelectedAlert(alert);
    if (!alert.read) {
      await markAsRead(alert.id);
    }
  };
  
  const handleAcknowledge = async (alertId) => {
    await acknowledge(alertId, userName);
    setSelectedAlert(null);
  };
  
  const handleDismiss = async (alertId) => {
    const reason = prompt(t('reasonForDismiss'));
    if (reason !== null) {
      await dismiss(alertId, reason);
      setSelectedAlert(null);
    }
  };
  
  const handleResolve = async (alertId) => {
    const resolution = prompt(t('resolutionDetails'));
    if (resolution) {
      await resolve(alertId, resolution);
      setSelectedAlert(null);
    }
  };
  
  const getFilteredAlerts = () => {
    let filtered = alerts;
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }
    
    // Severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(a => a.severity === severityFilter.toUpperCase());
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.title.toLowerCase().includes(query) ||
        a.message.toLowerCase().includes(query) ||
        (a.context?.patientName && a.context.patientName.toLowerCase().includes(query)) ||
        (a.context?.departmentName && a.context.departmentName.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };
  
  const filteredAlerts = getFilteredAlerts();
  
  return (
    <div className="alert-management">
      {/* Header */}
      <div className="management-header">
        <div className="header-title">
          <Bell size={28} />
          <h1>{t('alertManagement')}</h1>
        </div>
        <button onClick={refresh} className="btn btn-secondary">
          🔄 {t('refresh')}
        </button>
      </div>
      
      {/* Statistics */}
      <div className="alert-stats">
        <div className="stat-card critical">
          <div className="stat-icon">🚨</div>
          <div className="stat-info">
            <div className="stat-number">{counts.critical}</div>
            <div className="stat-label">{t('critical')}</div>
          </div>
        </div>
        <div className="stat-card high">
          <div className="stat-icon">⚠️</div>
          <div className="stat-info">
            <div className="stat-number">{counts.high}</div>
            <div className="stat-label">{t('highPriority')}</div>
          </div>
        </div>
        <div className="stat-card medium">
          <div className="stat-icon">ℹ️</div>
          <div className="stat-info">
            <div className="stat-number">{counts.medium}</div>
            <div className="stat-label">{t('medium')}</div>
          </div>
        </div>
        <div className="stat-card unread">
          <div className="stat-icon">🔔</div>
          <div className="stat-info">
            <div className="stat-number">{counts.unread}</div>
            <div className="stat-label">{t('unread')}</div>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="filter-section">
        <div className="filter-group">
          <label>
            <Filter size={16} />
            {t('statusLabel')}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ACTIVE">{t('activeAlerts')}</option>
            <option value="RESOLVED">{t('resolved')}</option>
            <option value="DISMISSED">{t('dismissed')}</option>
            <option value="all">{t('all')}</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>{t('severityLabel')}</label>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <option value="all">{t('all')}</option>
            <option value="critical">{t('critical')}</option>
            <option value="high">{t('high')}</option>
            <option value="medium">{t('medium')}</option>
          </select>
        </div>
        
        <div className="search-group">
          <Search size={16} />
          <input
            type="text"
            placeholder={t('searchAlerts')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="results-count">
          {t('alertsFound', { count: filteredAlerts.length })}
        </div>
      </div>
      
      {/* Alerts Grid */}
      <div className="alerts-grid">
        {loading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>{t('loadingAlerts')}</p>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="empty-state">
            <Bell size={64} color="#ccc" />
            <h3>{t('noAlertsFound')}</h3>
            <p>{t('noAlertsMatchFilters')}</p>
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <div
              key={alert.id}
              className={`alert-card ${alert.severity.toLowerCase()} ${!alert.read ? 'unread' : ''}`}
              onClick={() => handleAlertClick(alert)}
              style={{ borderTopColor: getSeverityColor(alert.severity) }}
            >
              <div className="card-header">
                <div className="alert-severity">
                  {getSeverityIcon(alert.severity)}
                  <span className="severity-label">{alert.severity}</span>
                </div>
                <span className={`status-badge ${getStatusBadgeClass(alert.status)}`}>
                  {alert.status}
                </span>
              </div>
              
              <div className="card-body">
                <h3 className="alert-card-title">
                  {alert.title}
                  {!alert.read && <span className="unread-indicator">●</span>}
                </h3>
                
                <p className="alert-card-message">{alert.message}</p>
                
                <div className="alert-context">
                  {alert.context?.patientName && (
                    <span className="context-tag">👤 {alert.context.patientName}</span>
                  )}
                  {alert.context?.departmentName && (
                    <span className="context-tag">🏬 {alert.context.departmentName}</span>
                  )}
                  {alert.context?.taskName && (
                    <span className="context-tag">📋 {alert.context.taskName}</span>
                  )}
                </div>
                
                <div className="card-meta">
                  <span className="alert-time">
                    {formatDateTime(alert.createdAt)}
                  </span>
                  {alert.acknowledgedBy && alert.acknowledgedBy.length > 0 && (
                    <span className="acknowledged-count">
                      {t('acknowledgedCount', { count: alert.acknowledgedBy.length })}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="card-actions">
                <button
                  className="btn btn-info btn-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAlertClick(alert);
                  }}
                >
                  <Eye size={14} />
                  View Details
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Alert Detail Modal */}
      {selectedAlert && (
        <>
          <div className="modal-overlay" onClick={() => setSelectedAlert(null)} />
          <div className="alert-detail-modal">
            <div className="modal-header">
              <div className="modal-title">
                {getSeverityIcon(selectedAlert.severity)}
                <h2>{selectedAlert.title}</h2>
              </div>
              <button 
                className="modal-close"
                onClick={() => setSelectedAlert(null)}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h4>{t('alertInformation')}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('severityLabel')}</label>
                    <span className={`severity-badge ${selectedAlert.severity.toLowerCase()}`}>
                      {selectedAlert.severity}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>{t('statusLabel')}</label>
                    <span className={`status-badge ${getStatusBadgeClass(selectedAlert.status)}`}>
                      {selectedAlert.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>{t('createdLabel')}</label>
                    <span>{formatDateTime(selectedAlert.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('escalationLabel')}</label>
                    <span>{selectedAlert.escalation}</span>
                  </div>
                </div>
              </div>
              
              <div className="detail-section">
                <h4>{t('messageLabel')}</h4>
                <p>{selectedAlert.message}</p>
              </div>
              
              {selectedAlert.suggestedActions && selectedAlert.suggestedActions.length > 0 && (
                <div className="detail-section">
                  <h4>{t('suggestedActions')}</h4>
                  <ul className="actions-list">
                    {selectedAlert.suggestedActions.map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedAlert.context && Object.keys(selectedAlert.context).length > 0 && (
                <div className="detail-section">
                  <h4>Context</h4>
                  <div className="context-grid">
                    {Object.entries(selectedAlert.context).filter(([key, value]) => value).map(([key, value]) => (
                      <div key={key} className="context-item">
                        <label>{key.replace(/([A-Z])/g, ' $1').trim()}:</label>
                        <span>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedAlert.acknowledgedBy && selectedAlert.acknowledgedBy.length > 0 && (
                <div className="detail-section">
                  <h4>{t('acknowledgedBy')}</h4>
                  <div className="acknowledged-list">
                    {selectedAlert.acknowledgedBy.map((ack, index) => (
                      <div key={index} className="acknowledged-item">
                        <span className="ack-user">✓ {ack.userName}</span>
                        <span className="ack-time">{formatDateTime(ack.acknowledgedAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              {selectedAlert.status === 'ACTIVE' && (
                <>
                  {!selectedAlert.acknowledgedBy?.some(ack => ack.userId === userId) && (
                    <button
                      className="btn btn-success btn-icon"
                      onClick={() => handleAcknowledge(selectedAlert.id)}
                    >
                      <Check size={16} />
                      Acknowledge
                    </button>
                  )}
                  <button
                    className="btn btn-success btn-icon"
                    onClick={() => handleResolve(selectedAlert.id)}
                  >
                    <Check size={16} />
                    Resolve
                  </button>
                  <button
                    className="btn btn-secondary btn-icon"
                    onClick={() => handleDismiss(selectedAlert.id)}
                  >
                    <X size={16} />
                    Dismiss
                  </button>
                </>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedAlert(null)}
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Format timestamp to readable date/time
 */
function formatDateTime(timestamp) {
  if (!timestamp) return 'N/A';
  
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp.toDate?.() || timestamp;
  return date.toLocaleString();
}

export default AlertManagement;


